import { faWeightScale } from "@fortawesome/pro-light-svg-icons";
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import type { WeightUnit } from "@kanpla/api-contract";

import { T } from "@repo/transifex";
import { Modal } from "~/components/kiosk/ui/modals/shared/modal";
import { ModalButton } from "~/components/kiosk/ui/modals/shared/modal-button";
import { ModalErrorGeneric } from "~/components/kiosk/ui/modals/error/modal-error-generic";
import { ModalErrorScaleUnit } from "~/components/kiosk/ui/modals/error/modal-error-scale-unit";
import { ModalTitle } from "~/components/kiosk/ui/modals/shared/modal-title";
import { ModalWeightCapture } from "~/components/kiosk/ui/modals/weight/modal-weight-capture";
import { useAppContext } from "~/providers/app";
import { ModalIcon } from "~/components/kiosk/ui/modals/shared/modal-icon";
import { ModalButtonGroup } from "~/components/kiosk/ui/modals/shared/modal-button-group";
import { useSendWebEvent } from "~/hooks";
import {
  convertWeight,
  getPricingBasis,
  isSameUnitSystem
} from "~/utils/weight-units";

import { useProductDetail } from "../__app.kiosk.products.$productId/product-detail-context";

export const KioskWeightProductPage = () => {
  const navigate = useNavigate();
  const { item, close, setWeight } = useProductDetail();
  const sendWebEvent = useSendWebEvent();

  const {
    hardware: {
      scales: { scale, unit: pairedUnit, weight: reading }
    }
  } = useAppContext();

  // The paired unit is known before the first frame arrives and survives a
  // reading reset; the reading's own unit starts as grams either way, which
  // would flash the mismatch dialog on every imperial weigh. Kilogram scales
  // report in grams.
  const scaleUnit: WeightUnit = pairedUnit === "kg" ? "g" : pairedUnit;

  const product = item.product;
  const basis =
    product.type === "weighted" ? getPricingBasis(product.unitSystem) : null;

  // Disconnect when leaving the page
  useEffect(() => {
    return () => {
      sendWebEvent({ type: "SCALE_DISCONNECT_REQUEST" });
    };
  }, [sendWebEvent]);

  // If product is not weighted, show error (shouldn't happen if routes are correct)
  if (product.type !== "weighted" || !basis) {
    return <ModalErrorGeneric onClose={close} />;
  }

  if (!scale) {
    return (
      <Modal backdropVariant="error">
        <div className="flex flex-col items-center gap-16 px-32 text-center">
          <ModalIcon icon={faWeightScale} variant="error" />

          <ModalTitle size="9xl" variant="error" weight="black">
            <T _str="No scale connected" />
          </ModalTitle>

          <p className="text-6xl text-kiosk-text-white">
            <T _str="Ordering this product requires a scale to be connected" />
          </p>

          <ModalButtonGroup className="mt-16">
            <ModalButton variant="error" onClick={close}>
              <T _str="Close" />
            </ModalButton>
          </ModalButtonGroup>
        </div>
      </Modal>
    );
  }

  if (!isSameUnitSystem(scaleUnit, basis.weightUnit)) {
    return (
      <ModalErrorScaleUnit
        scaleUnit={scaleUnit}
        unitSystem={product.unitSystem}
        onClose={close}
      />
    );
  }

  // The tare is stored in the unit the product is priced per; the reading is
  // in the scale's unit, so the tare moves over (an exact within-system factor).
  const netWeight =
    reading.weight -
    convertWeight(product.containerWeight, basis.weightUnit, scaleUnit);

  const captured =
    reading.status === "stable" && netWeight > 0
      ? { weight: netWeight, unit: scaleUnit, decimals: reading.decimals }
      : null;

  const handleConfirm = () => {
    if (!captured) return;
    setWeight(captured);
    navigate("..");
  };

  return (
    <ModalWeightCapture
      productName={product.name}
      captured={captured}
      onConfirm={handleConfirm}
      onCancel={close}
    />
  );
};
